import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { intlShape, injectIntl } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { AspectRatioWrapper, AvatarSmall, NamedLink, ResponsiveImage } from '..';
import Icons from '../Icons/Icons';
import { isArrayLength } from '../../util/genericHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../ducks/user.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import {
  addItemToFavorites,
  deleteItemFromFavorites,
  favoritesSelector,
} from '../../containers/FavoritesPage/FavoritesPage.duck';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { richText } from '../../util/richText';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    isLandingListingCard,
    showLightVersion = false,
    isBrand,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const currentUser = useSelector(userSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUserId = currentUser?.id?.uuid;
  const { favorites = [], currentFavoriteId } = useSelector(favoritesSelector);
  const favoritesList = favorites || [];
  const listingId = listing?.id?.uuid;
  const isOwnListing = currentUserId && listing?.author?.id?.uuid === currentUserId;
  const isAlreadyAddedToFavoriteslist =
    isArrayLength(favoritesList) && favoritesList?.includes(listingId);

  const handleFavoritesList = async e => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isAlreadyAddedToFavoriteslist) {
        await dispatch(
          deleteItemFromFavorites({
            isAuthenticated,
            id: listingId,
            dispatch,
            favorites,
            config,
            isAuthenticated,
            userId: currentUserId,
          })
        );
      } else {
        await dispatch(
          addItemToFavorites({
            favorites,
            currentFavoriteId: listingId,
            userId: currentUserId,
            isAuthenticated,
            config,
          })
        );
      }
    } catch (error) {
      console.log('Error while updating favorites');
    }
  };
  const classes = classNames(rootClassName || css.root, className, {
    [css.landingListingCard]: isLandingListingCard,
    [css.brandListingCard]: isBrand,
  });

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description, publicData } = currentListing.attributes || {};

  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'scaled-medium',
  } = config.layout.listingImage;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;
  const navigateToListing = () => {
    history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {}));
  };
  return (
    <>
      {isBrand ? (
        <div className={classes} onClick={navigateToListing}>
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
            {/* <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} /> */}
          </AspectRatioWrapper>

          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.cardTitle}>
                <h2>
                  {richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </h2>
                <span className={css.brandPremier}>
                  <Icons name="check" /> Brand Premier
                </span>
              </div>
              <p className={css.cardDescription}>{description}</p>
            </div>

            <NamedLink className={css.findLink} name="LandingPage">
              Explore <Icons name="rightArrow" />{' '}
            </NamedLink>
          </div>
        </div>
      ) : (
        <div className={classes} onClick={navigateToListing}>
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
            {...setActivePropsMaybe}
          >
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
            {/* <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} /> */}
          </AspectRatioWrapper>

          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.authorDetails}>
                <div className={css.authorImg}>
                  <AvatarSmall user={author} />
                </div>

                <div
                  className={classNames(css.authorInfo, {
                    [css.showLightVersion]: showLightVersion,
                  })}
                >
                  <h2>{authorName}</h2>
                </div>
                <span className={css.type}>Pro</span>
              </div>
              {/* <span className={css.otherInfo}>lorem</span> */}
              {!isOwnListing && (
                <div className={css.favoritesContainer}>
                  <span
                    className={classNames(
                      css.favoriteIcon,
                      isAlreadyAddedToFavoriteslist ? css.favorited : null
                    )}
                    onClick={e => {
                      e.stopPropagation(); // Prevent NamedLink navigation

                      if (isAuthenticated) {
                        handleFavoritesList(e);
                      } else {
                        history.push(
                          createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
                        );
                      }
                    }}
                  >
                    {currentFavoriteId === listingId ? (
                      <IconSpinner />
                    ) : isAlreadyAddedToFavoriteslist ? (
                      <Icons name="filledHeartIcon" />
                    ) : (
                      <Icons name="heart" />
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* <div className={css.cardPostTime}>
          {isArrayLength(sportsOptions)
            ? sportsOptions.map((s, i) => (
                <span className={css.time} key={i}>
                  {s?.label}
                </span>
              ))
            : null}
        </div> */}
            {/* <div className={css.cardTitleSec}>
          <h2 className={css.cardTitle}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </h2>
        </div>
        <p className={css.cardDescription}>{listingDescription}</p>
        <div className={css.followers}>
          {isArrayLength(listingSocialMediaDetails) &&
            listingSocialMediaDetails.length > 0 &&
            listingSocialMediaDetails.map(({ followers, handle, platform }, i) => {
              const iconName = platformIconMap[platform] || platformIconMap.default;

              return (
                <span key={i}>
                  {iconName && <Icons name={iconName} />} {formatNumber(followers)}
                </span>
              );
            })}
        </div> */}
          </div>
        </div>
      )}
    </>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
